import { Building } from '@shared/models/design/building-design.model';
import { DraftShape, Shape, Storey } from '@shared/models/design/storey.model';
import {
  CUTOUT_MIN_MARGIN_METERS,
  findEnclosingSolidShape,
  isCutoutInsideStoreyWithMargin,
} from './cutout.helpers';
import { designGuards } from './design-typeguards.helpers';
import { createShape } from './factories/shape.factory';
import {
  cutoutIntersectsExistingCutoutOnStorey,
  doesShapeIntersectBuildingSolids,
} from './shape.helpers';
import {
  applyGroundFootprintToEmptyStoreys,
  getStoreyById,
  getStoreyElevationRange,
  isEmptyStoreysGeometry,
} from './storey.helpers';

/**
 * Result of closing a map draft onto storeys.
 * `overlap` keeps the draft open. `rejected` drops it. `applied` is the next stack.
 */
export type CompletedDraftApplication =
  | { status: 'overlap' }
  | { status: 'rejected' }
  | { status: 'applied'; storeys: Storey[] };

/**
 * Write a closed map draft onto the selected storey.
 * A cutout on a mezzanine, outside its solid, or inside the 1 m margin is
 * rejected. A solid that overlaps a sibling building is `overlap` so the draft
 * can stay open. The first solid on an empty building is copied onto shapeless
 * floors above. Mezzanines keep their own outline.
 */
export const applyCompletedDraftToStoreys = (
  storeys: Storey[],
  draftShape: DraftShape,
  options: {
    selectedStoreyId?: string;
    otherBuildings?: Building[];
    propagateToStoreyIds?: ReadonlyArray<Storey['id']>;
  } = {},
): CompletedDraftApplication => {
  const selectedStoreyId = options.selectedStoreyId ?? storeys[0]?.id;
  if (!selectedStoreyId) return { status: 'rejected' };
  if ((draftShape.points?.length ?? 0) < 3) return { status: 'rejected' };

  const selectedStorey = getStoreyById(storeys, selectedStoreyId);
  const isCutoutDraft = draftShape.cutout === true;
  if (
    !isCutoutDraft &&
    doesShapeIntersectBuildingSolids(
      draftShape,
      options.otherBuildings,
      selectedStorey
        ? getStoreyElevationRange(storeys, selectedStorey)
        : undefined,
    )
  ) {
    return { status: 'overlap' };
  }

  const targetStoreyIds = new Set<Storey['id']>([
    selectedStoreyId,
    ...(options.propagateToStoreyIds ?? []),
  ]);

  if (
    isCutoutDraft &&
    selectedStorey &&
    designGuards.is.mezzanineStorey(selectedStorey)
  ) {
    return { status: 'rejected' };
  }

  if (
    isCutoutDraft &&
    !isValidCutoutDraft(storeys, draftShape, targetStoreyIds, selectedStorey)
  ) {
    return { status: 'rejected' };
  }

  return {
    status: 'applied',
    storeys: storeysWithDraft(
      storeys,
      draftShape,
      selectedStoreyId,
      targetStoreyIds,
      isCutoutDraft,
    ),
  };
};

/** Validate and apply a preset's solid and cutouts atomically without saving. */
export const applyCompletedDraftsToStoreys = (
  storeys: Storey[],
  drafts: DraftShape[],
  options: Parameters<typeof applyCompletedDraftToStoreys>[2] = {},
): CompletedDraftApplication => {
  if (!drafts.length) return { status: 'rejected' };
  let next = storeys;
  for (const draft of drafts) {
    const result = applyCompletedDraftToStoreys(next, draft, options);
    if (result.status !== 'applied') return result;
    next = result.storeys;
  }
  return { status: 'applied', storeys: next };
};

/**
 * A cutout must sit inside a solid, keep {@link CUTOUT_MIN_MARGIN_METERS},
 * and must not cross a cutout already on a target floor.
 */
const isValidCutoutDraft = (
  storeys: Storey[],
  draftShape: DraftShape,
  targetStoreyIds: ReadonlySet<Storey['id']>,
  selectedStorey: Storey | undefined,
): boolean => {
  const cutoutShape = createShape({
    ...draftShape,
    cutout: true,
    points: [...draftShape.points],
  });
  if (!findEnclosingSolidShape(selectedStorey, cutoutShape)) return false;
  if (
    !isCutoutInsideStoreyWithMargin({
      cutout: cutoutShape,
      storey: selectedStorey,
      minMarginMeters: CUTOUT_MIN_MARGIN_METERS,
    })
  ) {
    return false;
  }

  for (const targetStoreyId of targetStoreyIds) {
    const targetStorey = getStoreyById(storeys, targetStoreyId);
    if (!targetStorey || designGuards.is.mezzanineStorey(targetStorey)) {
      continue;
    }
    if (cutoutIntersectsExistingCutoutOnStorey(targetStorey, cutoutShape)) {
      return false;
    }
  }
  return true;
};

/**
 * Append the draft to each target floor. The first solid on an empty building
 * is also copied onto shapeless non-mezzanine floors.
 */
const storeysWithDraft = (
  storeys: Storey[],
  draftShape: DraftShape,
  selectedStoreyId: Storey['id'],
  targetStoreyIds: ReadonlySet<Storey['id']>,
  isCutoutDraft: boolean,
): Storey[] => {
  let mutated = false;
  const next = storeys.map((storey) => {
    if (!targetStoreyIds.has(storey.id)) return storey;
    // Mezzanines cannot own a void. A propagated cutout stays on the regular floors.
    if (isCutoutDraft && designGuards.is.mezzanineStorey(storey)) return storey;
    mutated = true;
    return {
      ...storey,
      shapes: [
        ...storey.shapes,
        shapeForStorey(draftShape, storey.id, selectedStoreyId, isCutoutDraft),
      ],
    };
  });
  if (!mutated) return storeys;
  if (isCutoutDraft || !isEmptyStoreysGeometry(storeys)) return next;

  const ground =
    next.find((storey) => storey.id === selectedStoreyId) ?? next[0];
  if (!ground) return next;
  return applyGroundFootprintToEmptyStoreys(next, ground);
};

/**
 * Keep the draft id on the selected storey. Floors above get a new id.
 */
const shapeForStorey = (
  draftShape: DraftShape,
  storeyId: Storey['id'],
  selectedStoreyId: Storey['id'],
  isCutoutDraft: boolean,
): Shape =>
  createShape({
    ...draftShape,
    id: storeyId === selectedStoreyId ? draftShape.id : undefined,
    points: [...draftShape.points],
    cutout: isCutoutDraft,
  });
